import { ReactNode, useRef, useEffect } from 'react';

import IconClose from 'approot/shared/icons/icon-close';
import * as Styled from './dismissable-snackbar.style';
import { Box } from 'approot/shared/design-system/box/box.style';

type Props = {
  isVisible: boolean;
  onDismiss: () => void;
  backgroundColor?: string;
  textColor?: string;
  children: ReactNode;
  actions?: ReactNode;
  'data-selector'?: string;
};

const DISMISSABLE_SNACKBAR_ID = 'dismissable-snackbar';

/**
 * A dismissible notification snackbar component with customizable styling and actions.
 *
 * Provides a toast-style notification that appears at the bottom of the viewport with
 * a close button and optional action buttons. Automatically positions itself relative
 * to the window height and adjusts on window resize. Includes accessibility features
 * with ARIA live regions and proper labeling for screen readers.
 *
 * @param isVisible - Controls whether the snackbar is displayed
 * @param onDismiss - Callback function triggered when the close button is clicked
 * @param backgroundColor - Optional custom background color override
 * @param textColor - Optional custom text color override
 * @param children - The main content/message to display in the snackbar
 * @param actions - Optional React nodes for action buttons (displayed to the right of content)
 * @param data-selector - Optional data attribute for testing/automation purposes
 *
 * @example
 * ```tsx
 * // Basic notification
 * <DismissableSnackbar
 *   isVisible={showNotification}
 *   onDismiss={() => setShowNotification(false)}
 * >
 *   Your changes have been saved successfully!
 * </DismissableSnackbar>
 *
 * // Notification with custom styling
 * <DismissableSnackbar
 *   isVisible={showError}
 *   onDismiss={() => setShowError(false)}
 *   backgroundColor={purple1}
 *   textColor={grey1}
 * >
 *   An error occurred while saving your changes.
 * </DismissableSnackbar>
 *
 * // Notification with action buttons
 * <DismissableSnackbar
 *   isVisible={showUndoNotification}
 *   onDismiss={() => setShowUndoNotification(false)}
 *   actions={
 *     <button onClick={handleUndo}>Undo</button>
 *   }
 * >
 *   Item deleted successfully.
 * </DismissableSnackbar>
 *
 * // Complex notification with multiple actions
 * <DismissableSnackbar
 *   isVisible={showComplexNotification}
 *   onDismiss={() => setShowComplexNotification(false)}
 *   actions={
 *     <Box $gap={8}>
 *       <button onClick={handleRetry}>Retry</button>
 *       <button onClick={handleCancel}>Cancel</button>
 *     </Box>
 *   }
 * >
 *   Upload failed. Would you like to retry?
 * </DismissableSnackbar>
 * ```
 */
const DismissableSnackbar = ({
  isVisible,
  onDismiss,
  backgroundColor,
  textColor,
  children,
  actions,
  'data-selector': dataSelector,
}: Props) => {
  const snackbarElement = useRef<HTMLDivElement | null>(null);

  const handleResize = () => {
    if (snackbarElement && snackbarElement.current) {
      snackbarElement.current.style.top = `${window.innerHeight - 40}px`;
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isVisible) return null;

  return (
    <div role="alert" aria-live="polite">
      <Styled.DismissableSnackbarContainer
        data-selector={dataSelector || 'dismissable-snackbar'}
        ref={ref => {
          snackbarElement.current = ref;
        }}
        id={DISMISSABLE_SNACKBAR_ID}
        $backgroundColor={backgroundColor}
        $textColor={textColor}
        $top={window.innerHeight - 40}
      >
        <Styled.DismissableSnackbarContent>
          <Styled.DismissableSnackbarText>
            {children}
          </Styled.DismissableSnackbarText>
          {actions && (
            <Box $gap={8} $alignItems="center" $flexShrink={0}>
              {actions}
            </Box>
          )}
        </Styled.DismissableSnackbarContent>
        <Styled.DismissableSnackbarCloseButton
          type="button"
          data-selector="close-button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          <IconClose width={14} height={14} />
        </Styled.DismissableSnackbarCloseButton>
      </Styled.DismissableSnackbarContainer>
    </div>
  );
};

export default DismissableSnackbar;
